/**
 * # AsModalOverlay Component
 * 
 * A flexible modal dialog component that creates an overlay on top of the UI.
 * This component provides a reusable modal container with backdrop, close button,
 * and content slot for displaying various types of modal content.
 * 
 * ## Features
 * - Overlay with semi-transparent backdrop
 * - Animated show/hide transitions
 * - Close button in the top-right corner
 * - Click outside to dismiss
 * - Customizable content via slots
 * - Programmatic open/close methods
 * - Two modes for content visibility: auto and manual
 * 
 * ## Component Hierarchy
 * ```
 * AsModalOverlay (as-modal-overlay)
 * └── .modal-content - Container for modal content
 *     ├── .close-button - Button to close the modal
 *     └── <slot> - Content provided by the consumer
 * ```
 * 
 * ## Attributes
 * - `show`: When present, the modal is visible
 * - `mode`: Controls how child elements are displayed:
 *    - `auto` (default): Only show the child element with the ID matching `visible-child`
 *    - `manual`: Show all children, allowing for manual visibility control via CSS
 * - `visible-child`: ID of the child element to show when in `auto` mode
 * 
 * ## CSS Custom Properties
 * - `--as-modal-overlay-z-index`: Z-index for the modal overlay (default: 999999)
 * - `--modal-bg-col`: Background color of the modal (default: white)
 * - `--modal-close-btn-text-col`: Color of the close button (default: #666)
 *
 * ## Methods
 * - `open(id?)`: Shows the modal by adding the 'show' attribute
 * - `close()`: Hides the modal by removing the 'show' attribute
 * - `show(id?)`: Alias for open()
 * - `hide()`: Alias for close()
 * - `setVisiblePanel(id)`: Sets the specified panel as visible (in auto mode)
 * 
 * ## Events
 * - `as-modal-closed`: Dispatched when the modal is closed via backdrop click or close button
 * - `as-set-visible-child`: Event listened for by the component with an id property in detail to change visible panel
 * - `as-open-overlay`: Event listened for by the component to open the modal
 * - `open-modal`: Alternative event listened for by the component to open the modal
 * - `as-close-overlay`: Event listened for by the component to close the modal
 * 
 * ## Usage Examples
 * 
 * ### Basic Usage (Auto Mode)
 * ```html
 * <as-modal-overlay id="myModal" visible-child="panel1">
 *   <div id="panel1" class="modal-panel">Panel 1 Content</div>
 *   <div id="panel2" class="modal-panel">Panel 2 Content</div>
 *   <div id="panel3" class="modal-panel">Panel 3 Content</div>
 * </as-modal-overlay>
 * 
 * <script>
 *   // Switch to a different panel
 *   document.getElementById('myModal').setAttribute('visible-child', 'panel2');
 * </script>
 * ```
 * 
 * ### Manual Mode
 * ```html
 * <as-modal-overlay id="myModal" mode="manual">
 *   <div id="panel1" class="visible">Always visible content</div>
 *   <div id="panel2">
 *     Content to show/hide manually with JavaScript:
 *     document.querySelector('#panel2').classList.toggle('visible');
 *   </div>
 * </as-modal-overlay>
 * ```
 * 
 * ### Opening and Closing Programmatically
 * ```javascript
 * // Get a reference to the modal
 * const modal = document.querySelector('as-modal-overlay');
 * 
 * // Open the modal
 * modal.open();
 * 
 * // Close the modal
 * modal.close();
 * 
 * // Listen for close events
 * modal.addEventListener('as-modal-closed', () => {
 *   console.log('Modal was closed');
 * });
 * ```
 * 
 * ### With Custom Styling
 * ```html
 * <style>
 *   as-modal-overlay {
 *     --modal-bg-col: #f5f5f5;
 *     --modal-close-btn-text-col: #333;
 *   }
 * </style>
 * <as-modal-overlay>
 *   <div class="custom-content">
 *     <!-- Your custom modal content -->
 *   </div>
 * </as-modal-overlay>
 * ```
 * 
 * ### With Form Content
 * ```html
 * <as-modal-overlay id="login-modal">
 *   <form id="login-form">
 *     <h2>Login</h2>
 *     <input type="email" placeholder="Email" required>
 *     <input type="password" placeholder="Password" required>
 *     <button type="submit">Login</button>
 *   </form>
 * </as-modal-overlay>
 * 
 * <script>
 *   const loginForm = document.getElementById('login-form');
 *   const modal = document.getElementById('login-modal');
 *   
 *   loginForm.addEventListener('submit', (e) => {
 *     e.preventDefault();
 *     // Process form submission
 *     modal.close();
 *   });
 * </script>
 * ```
 */

// const openWithVisibleChild = (ev:CustomEvent<{visibleChild:string}>) => (this as AsModalOverlay).open(ev.detail?.visibleChild)

import styleText from './as-modal-overlay.css?raw';

/**
 * A modal overlay component that covers the UI and can be dismissed
 * @fires as-modal-closed - When the modal is closed either via backdrop click or close button
 */
export class AsModalOverlay extends HTMLElement {
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();
  private listeners: {[key:string]:EventListener} = {};
  private listenerNames:string[] = [];
  private panelObserver: MutationObserver | null = null;
  private pendingPanelId: string | null = null;
  private scrollPosition: number = 0;
  
  /**
   * Returns attributes to observe for changes
   */
  static get observedAttributes(): string[] {
    //TODO: implement prevent-close
    return ['show', 'visible-child', 'mode','show-close','prevent-close'];
  }
  render =()=>{
    const mode = this.getAttribute('mode') ?? 'auto';
    
    // Set the mode attribute if it's not explicitly set
    if (!this.hasAttribute('mode')) {
      this.setAttribute('mode', 'auto');
    }

    this.shadowRoot!.innerHTML =  /*html*/`
    <div class="modal-content">
      ${(this.hasAttribute("show-close")&&this.getAttribute("show-close")!=="false")?`<button class="close-button" aria-label="Close modal">×</button>`:''}
      <slot id="content-slot"></slot>
    </div>
  `;
    if (mode === 'auto' && this.hasAttribute('visible-child')) {
      this.setVisiblePanel(this.getAttribute('visible-child'));
    }
  }
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      AsModalOverlay.sheet
    ];
    this.render();
  }
  
  /**
   * Handles changes to observed attributes
   */
  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (name === 'show' && oldValue !== newValue) {
      if (newValue !== null) {
        // Show was added
        this.classList.add('ready-for-transition');
      } else {
        // Show was removed
        setTimeout(() => {
          this.dispatchEvent(new CustomEvent('as-modal-closed', {
            bubbles: true,
            composed: true
          }));
        }, 300);
      }
    }
    if (name === 'visible-child' && oldValue !== newValue && this.getAttribute('mode') === 'auto') {
      this.setVisiblePanel(newValue);
    }
    if (name === 'mode' && oldValue !== newValue) {
      // If switching to auto mode and there's a visible-child attribute
      if (newValue === 'auto' && this.hasAttribute('visible-child')) {
        this.setVisiblePanel(this.getAttribute('visible-child'));
      }
    }
  }

  connectedCallback() {
    // console.log("AsModalOverlay connected");
    // Handle backdrop and close button clicks
    this.addEventListener('click', (e) => {
      const closeButton = this.shadowRoot?.querySelector('.close-button');
      const modalContent = this.shadowRoot?.querySelector('.modal-content');
      
      // composedPath() returns the event's path through the DOM including shadow DOM
      // The path array starts from the target element and bubbles up through its ancestors
      // Example path for clicking the modal content:
      // [0] -> div.modal-content (actual clicked element)
      // [1] -> #shadow-root
      // [2] -> as-modal-overlay (host element)
      // [3] -> body
      // [4] -> html
      // [5] -> document
      // [6] -> window
      const path = e.composedPath();
      const clickedElement = path[0]; // Get the actual element that was clicked
      
      // Close if clicking the host element (backdrop) or close button
      // But not if clicking the modal content or its children
      if ((clickedElement === this && !modalContent?.contains(clickedElement as Node)) || 
          clickedElement === closeButton) {
        // console.log(e);
        this.close();
      }
    });
    this.addEventListener('as-set-visible-child', (e: CustomEvent<{id: string}>) => {
      // console.log("as-set-visible-child", e.detail);
      this.setAttribute('visible-child', e.detail.id);
    });
    
    this.addEventListener('open-modal', (ev:CustomEvent<{visibleChild?:string}>) => this.open(ev.detail?.visibleChild));
    this.addEventListener('as-open-overlay', (ev:CustomEvent<{visibleChild?:string}>) => this.open(ev.detail?.visibleChild));
    this.addEventListener('as-open-modal', (ev:CustomEvent<{visibleChild?:string}>) => this.open(ev.detail?.visibleChild));
    
    this.addEventListener('as-close-overlay', () => this.close());
    this.addEventListener('as-close-modal', () => this.close());
    this.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.close();
      }
      if (e.key === 'Enter') {
        this.dispatchEvent(new CustomEvent('as-modal-enter-pressed', {
          bubbles: true,
          composed: true
        }));
      }
    });
    // After the component is connected to the DOM, wait until the next frame
    // to enable transitions. This prevents the transition from running 
    // during initial render.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('ready-for-transition');
      });
    });

    // Handle the visible-child attribute if it's present initially and mode is auto
    const mode = this.getAttribute('mode') ?? 'auto';
    if (mode === 'auto' && this.hasAttribute('visible-child')) {
      this.setVisiblePanel(this.getAttribute('visible-child'));
    }
  }
    
  disconnectedCallback(){
    this.querySelector('.close-button')?.removeEventListener('click', (e) => {
      if (e.target === this) {
        this.close();
      }
    });
    if (this.panelObserver) {
      this.panelObserver.disconnect();
      this.panelObserver = null;
    }
    
    const contentSlot = this.shadowRoot?.querySelector('#content-slot') as HTMLSlotElement;
    if (contentSlot) {
      contentSlot.removeEventListener('slotchange', this.handleSlotChange);
    }
  }
  
  /**
   * Opens the modal
   */
  open(id?:string): void {
    // Only proceed if the modal is not already shown
    if (!this.hasAttribute('show')) {
      // Ensure the ready-for-transition class is added
      if (!this.classList.contains('ready-for-transition')) {
        this.classList.add('ready-for-transition');
      }
      if(id)this.setAttribute('visible-child', id);

      // Lock body scroll to prevent iOS overscroll issues
      this.scrollPosition = document.body.scrollTop || document.documentElement.scrollTop;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.width = '100%';

      // Add the show attribute to trigger the transition
      this.setAttribute('show', '');
    }
  }
  show(id?:string){ // to prevent confusion with the attributes
    this.open(id);
  }

  /**
   * Closes the modal and emits a modal-close event
   */
  close(hideContent?:boolean): void {
    // Only proceed if the modal is currently shown
    if (this.hasAttribute('show')) {
      // Ensure the ready-for-transition class is added before closing
      if (!this.classList.contains('ready-for-transition')) {
        this.classList.add('ready-for-transition');
      }

      this.removeAttribute('show');
    }
  }
  hide(hideContent?:boolean){ // to prevent confusion with the attributes
    this.close(hideContent);
  }

  private setVisiblePanel(id?: string): void {
    if (!id) return;
    
    const contentSlot = this.shadowRoot!.querySelector('#content-slot') as HTMLSlotElement;
    if (!contentSlot) {
      console.warn('Content slot not found');
      return;
    }
    
    const assignedElements = contentSlot.assignedElements();
    
    // If we have assigned elements, process them
    if (assignedElements.length > 0) {
      this.applyVisibilityToElements(assignedElements, id);
      return;
    }
    
    // If no elements yet, set up observers and store the pending ID
    this.pendingPanelId = id;
    
    // Set up slot change listener if not already listening
    contentSlot.addEventListener('slotchange', this.handleSlotChange);
    
    // Set up mutation observer if we don't have one yet
    if (!this.panelObserver) {
      this.panelObserver = new MutationObserver(mutations => {
        if (this.pendingPanelId) {
          const newAssignedElements = contentSlot.assignedElements();
          if (newAssignedElements.length > 0) {
            this.applyVisibilityToElements(newAssignedElements, this.pendingPanelId);
            this.pendingPanelId = null;
            
            // Disconnect observer once we've successfully set visibility
            if (this.panelObserver) {
              this.panelObserver.disconnect();
              this.panelObserver = null;
            }
          }
        }
      });
      
      // Observe the host element for child additions/removals
      this.panelObserver.observe(this, { childList: true, subtree: false });
    }
  }

  // Handler for slot change events
  private handleSlotChange = (event: Event) => {
    const slot = event.target as HTMLSlotElement;
    const assignedElements = slot.assignedElements();
    
    if (assignedElements.length > 0 && this.pendingPanelId) {
      this.applyVisibilityToElements(assignedElements, this.pendingPanelId);
      this.pendingPanelId = null;
      
      // Remove listener once we've processed the elements
      slot.removeEventListener('slotchange', this.handleSlotChange);
    }
  };

  // Apply visibility to elements
  private applyVisibilityToElements(elements: Element[], targetId: string): void {
    let targetFound = false;
    
    elements.forEach(el => {
      if (el.id === targetId) {
        el.classList.add('visible');
        targetFound = true;
      } else {
        el.classList.remove('visible');
      }
    });
    
    // Log warning if target wasn't found
    if (!targetFound) {
      console.warn(`Panel with ID '${targetId}' not found among assigned elements`);
    }
  }
}

customElements.define('as-modal-overlay', AsModalOverlay); 
